import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/apiClient";
import { formatGrossFromNet } from "@/lib/money";
import {
  MAX_POS_TILE_PAGES,
  MAX_POS_TILES,
  emptyTilePage,
  normalizeTilePages,
  pageDisplayLabel,
  type PosTilePage,
} from "@/lib/posTiles";
import type { Article } from "@/types/camping";

interface PosSettingsResponse {
  pos: { tile_pages: PosTilePage[] };
}

export interface QuickSaleTileConfigModalProps {
  open: boolean;
  onClose: () => void;
  articles: Article[];
  tilePages: PosTilePage[];
  onSaved: (tilePages: PosTilePage[]) => void;
}

export function QuickSaleTileConfigModal({
  open,
  onClose,
  articles,
  tilePages,
  onSaved,
}: QuickSaleTileConfigModalProps) {
  const [pages, setPages] = useState<PosTilePage[]>(() => [emptyTilePage()]);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [activeSlot, setActiveSlot] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const articleById = useMemo(() => new Map(articles.map((a) => [a.id, a])), [articles]);

  const sortedArticles = useMemo(
    () => [...articles].sort((a, b) => a.name.localeCompare(b.name, "de")),
    [articles],
  );

  useEffect(() => {
    if (!open) return;
    const next = normalizeTilePages(tilePages);
    setPages(next);
    setActivePageIndex(0);
    setActiveSlot(null);
    setError(null);
    setSubmitting(false);
  }, [open, tilePages]);

  const activePage = pages[activePageIndex] ?? pages[0]!;
  const slots = activePage.tile_article_ids;

  const save = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.patch<PosSettingsResponse>("/api/v1/settings/pos-tiles", {
        tile_pages: pages,
      });
      onSaved(normalizeTilePages(res.pos.tile_pages));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }, [onClose, onSaved, pages]);

  function updateActivePageSlots(updater: (prev: (string | null)[]) => (string | null)[]) {
    setPages((prev) =>
      prev.map((page, idx) =>
        idx === activePageIndex
          ? { ...page, tile_article_ids: updater(page.tile_article_ids) }
          : page,
      ),
    );
  }

  function assignArticle(articleId: string) {
    if (activeSlot == null) return;
    updateActivePageSlots((prev) => {
      const next = [...prev];
      next[activeSlot] = articleId;
      return next;
    });
    setActiveSlot(null);
  }

  function clearSlot(index: number) {
    updateActivePageSlots((prev) => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
    if (activeSlot === index) setActiveSlot(null);
  }

  function updatePageLabel(label: string) {
    setPages((prev) =>
      prev.map((page, idx) => (idx === activePageIndex ? { ...page, label } : page)),
    );
  }

  function addPage() {
    if (pages.length >= MAX_POS_TILE_PAGES) return;
    setPages((prev) => [...prev, emptyTilePage()]);
    setActivePageIndex(pages.length);
    setActiveSlot(null);
  }

  function removeActivePage() {
    if (pages.length <= 1) return;
    setPages((prev) => prev.filter((_, idx) => idx !== activePageIndex));
    setActivePageIndex((idx) => Math.max(0, idx - 1));
    setActiveSlot(null);
  }

  return (
    <Modal open={open} onClose={onClose} title="Schnellverkauf-Kacheln" size="lg">
      <p className="text-sm text-label-secondary">
        Bis zu {MAX_POS_TILE_PAGES} Seiten mit je {MAX_POS_TILES} Kacheln. Leere Kacheln werden
        nicht angezeigt.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <div className="flex min-w-0 flex-1 gap-1 overflow-x-auto rounded-xl border border-separator bg-grouped p-1">
          {pages.map((page, index) => (
            <button
              key={index}
              type="button"
              className={`shrink-0 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                index === activePageIndex
                  ? "bg-accent text-label-on-accent shadow-sm"
                  : "text-label-secondary hover:bg-fill"
              }`}
              onClick={() => {
                setActivePageIndex(index);
                setActiveSlot(null);
              }}
            >
              {pageDisplayLabel(page.label, index)}
            </button>
          ))}
        </div>
        {pages.length < MAX_POS_TILE_PAGES ? (
          <Button type="button" variant="secondary" size="sm" onClick={addPage}>
            + Seite
          </Button>
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="flex min-w-[12rem] flex-1 flex-col gap-1 text-sm">
          Seitenname
          <input
            type="text"
            className="eh-input"
            value={activePage.label}
            maxLength={40}
            placeholder={pageDisplayLabel("", activePageIndex)}
            onChange={(e) => updatePageLabel(e.target.value)}
          />
        </label>
        {pages.length > 1 ? (
          <Button type="button" variant="secondary" size="sm" onClick={removeActivePage}>
            Seite löschen
          </Button>
        ) : null}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
        {slots.map((articleId, index) => {
          const article = articleId ? articleById.get(articleId) : undefined;
          const isActive = activeSlot === index;
          return (
            <div key={index} className="flex flex-col gap-1">
              <button
                type="button"
                className={`flex min-h-[52px] flex-col justify-center rounded-xl border px-2 py-2 text-left text-sm transition sm:min-h-[56px] sm:px-3 ${
                  isActive
                    ? "border-accent bg-accent/10 ring-2 ring-accent"
                    : "border-separator bg-fill-tertiary hover:bg-fill-secondary"
                }`}
                onClick={() => setActiveSlot(isActive ? null : index)}
              >
                <span className="text-xs text-label-secondary">Kachel {index + 1}</span>
                <span className="mt-0.5 line-clamp-2 font-medium leading-tight">
                  {article?.name ?? "Artikel wählen…"}
                </span>
              </button>
              {articleId ? (
                <button
                  type="button"
                  className="text-xs text-status-red underline"
                  onClick={() => clearSlot(index)}
                >
                  Entfernen
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {activeSlot != null ? (
        <div className="mt-4 rounded-lg border border-separator bg-fill-tertiary p-3">
          <p className="text-sm font-medium text-label">
            Artikel für Kachel {activeSlot + 1} (
            {pageDisplayLabel(activePage.label, activePageIndex)})
          </p>
          <ul className="mt-2 max-h-48 overflow-y-auto rounded-md border border-separator bg-surface text-sm">
            {sortedArticles.length === 0 ? (
              <li className="px-3 py-2 text-label-secondary">
                Keine Schnellverkauf-Artikel – Kategorie „Für Schnellverkauf“ prüfen.
              </li>
            ) : (
              sortedArticles.map((article) => (
                <li key={article.id}>
                  <button
                    type="button"
                    className="block w-full px-3 py-2.5 text-left hover:bg-fill-tertiary active:bg-fill-secondary"
                    onClick={() => assignArticle(article.id)}
                  >
                    <span className="font-medium">{article.name}</span>
                    <span className="ml-2 font-mono text-xs text-label-secondary">
                      {article.article_number}
                      {article.current_price != null
                        ? ` · ${formatGrossFromNet(article.current_price, article.tax_rate_percent)}`
                        : ""}
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}

      {error ? <p className="mt-3 text-sm text-status-red">{error}</p> : null}

      <div className="eh-modal-actions mt-4 flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
        <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
          Abbrechen
        </Button>
        <Button type="button" loading={submitting} onClick={() => void save()}>
          Speichern
        </Button>
      </div>
    </Modal>
  );
}
